/**
 * Privacy Warning Dialog
 * Shows when user tries to share a route with custom addresses
 */

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Shield, MapPin, Home, AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useI18n } from "@/lib/i18n";

interface PrivacyWarningDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onConfirm: () => void;
    hasStartAddress: boolean;
    hasEndAddress: boolean;
}

export function PrivacyWarningDialog({
    open,
    onOpenChange,
    onConfirm,
    hasStartAddress,
    hasEndAddress,
}: PrivacyWarningDialogProps) {
    const { t } = useI18n();

    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent className="max-w-md">
                <AlertDialogHeader>
                    <div className="flex items-center justify-center mb-4">
                        <div className="relative">
                            <div className="absolute inset-0 bg-yellow-500/20 rounded-full blur-xl" />
                            <div className="relative bg-yellow-500/10 p-3 rounded-full">
                                <Shield className="h-6 w-6 text-yellow-600" />
                            </div>
                        </div>
                    </div>
                    <AlertDialogTitle className="text-center text-xl">
                        {t("privacyDialog.title")}
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-center space-y-4">
                        <p className="text-base">
                            {t("privacyDialog.intro")}
                        </p>

                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-left space-y-2">
                            <div className="flex items-start gap-2 text-sm text-yellow-800">
                                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                <div>
                                    <p className="font-semibold mb-1">{t("privacyDialog.notShared")}</p>
                                    <ul className="space-y-1 ml-4 list-disc">
                                        {hasStartAddress && (
                                            <li className="flex items-center gap-1.5">
                                                <MapPin className="h-3 w-3" />
                                                <span>{t("privacyDialog.startAddress")}</span>
                                            </li>
                                        )}
                                        {hasEndAddress && (
                                            <li className="flex items-center gap-1.5">
                                                <Home className="h-3 w-3" />
                                                <span>{t("privacyDialog.endAddress")}</span>
                                            </li>
                                        )}
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-left">
                            <div className="flex items-start gap-2 text-sm text-green-800">
                                <Shield className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                <div>
                                    <p className="font-semibold mb-1">{t("privacyDialog.shared")}</p>
                                    <ul className="space-y-1 ml-4 list-disc">
                                        <li>{t("privacyDialog.sharedItem1")}</li>
                                        <li>{t("privacyDialog.sharedItem2")}</li>
                                        <li>{t("privacyDialog.sharedItem3")}</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <p className="text-sm text-muted-foreground">
                            {t("privacyDialog.outro")}
                        </p>
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                    <AlertDialogCancel>{t("shops.cancel")}</AlertDialogCancel>
                    <AlertDialogAction
                        onClick={onConfirm}
                        className="bg-primary hover:bg-primary/90"
                    >
                        {t("privacyDialog.shareWithoutAddresses")}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
